import React, { useEffect, useState, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import "./Spot.css";
import LeftTable from "./LeftTable";
import RightTable from "./RightTable";
import BuyAndSell from "./BuyAndSell";
import BottomTable from "./BottomTable";
import TvChartContainer from "./TVChart";
import Depth from "./Depth";

import axios from "axios";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import FlipNumbers from "react-flip-numbers";

import { useParams, useNavigate } from "react-router-dom";
import { checkInternetConnection } from "../../CoreDataService";
import { Paper } from "@mui/material";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import { DataContext } from "./index";

const parsePair = (pairStr) => {
  if (pairStr && pairStr.includes("-")) {
    const parts = pairStr.split("-");
    return {
      currency: parts[0].toUpperCase(),
      base: parts.slice(1).join("-").toUpperCase(),
    };
  }
  return { currency: "BTC", base: "USDT" };
};

const SpotPublic = () => {
  const { pair } = useParams();
  const navigate = useNavigate();
  const theme = localStorage.getItem("theme");

  const { currency: initialCurrency, base: initialBase } = parsePair(pair);

  const [isConnected, setIsConnected] = useState(true);
  const [activeButton, setActiveButton] = useState("Price");
  const [chartData, setChartData] = useState(null);
  const [ltp, setLtp] = useState(null);
  const [rocData, setRocData] = useState(0);
  const [volumnData, setVolumnData] = useState(0);
  const [lowPrice, setLowPrice] = useState(0);
  const [highPrice, setHighPrice] = useState(0);
  const [isNegative, setIsNegative] = useState(false);
  const [finalBiddata, setFinalBiddata] = useState([]);
  const [finalAskdata, setFinalAskdata] = useState([]);
  const [currencycode, setCurrencycode] = useState(initialCurrency);
  const [basecurrency, setBasecurrency] = useState(initialBase);

  const stompClientRef = useRef(null);
  const reqNo = Math.floor(Math.random() * 1000) + 1;
  const webSocketEndPoint = "https://stream.paybito.com/SocketStream/ws";
  const topicForBtcUsd = "/topic/stream/BTCUSD";
  const topic = "/user/topic/stream";

  // Sync URL when pair state changes
  useEffect(() => {
    const newPair = `${currencycode}-${basecurrency}`;
    if (pair !== newPair) {
      navigate(`/trade/${newPair}`, { replace: true });
    }
  }, [currencycode, basecurrency]);

  // Sync state when URL changes (browser back/forward)
  useEffect(() => {
    const { currency, base } = parsePair(pair);
    if (currency !== currencycode || base !== basecurrency) {
      setCurrencycode(currency);
      setBasecurrency(base);
    }
  }, [pair]);

  const allProps = {
    isPublicView: true,
    chartData,
    setFinalBiddata,
    setFinalAskdata,
    setCurrencycode,
    setBasecurrency,
    currencycode,
    basecurrency,
    lowPrice,
    highPrice,
    setSellData: () => {},
    setBuyData: () => {},
    finalBiddata,
    finalAskdata,
    ltp,
    setRefresh: () => {},
    refresh: false,
    updateBalance: false,
    sellData: "",
    buyData: "",
    setUpdateBalance: () => {},
    updateOrder: false,
    setUpdateOrder: () => {},
  };

  const _connect = () => {
    const socket = new SockJS(webSocketEndPoint);
    stompClientRef.current = Stomp.over(socket);
    stompClientRef.current.debug = null;

    stompClientRef.current.connect(
      {},
      (frame) => {
        const topicToSubscribe =
          currencycode?.toUpperCase() === "BTC" &&
          basecurrency?.toUpperCase() === "USD"
            ? topicForBtcUsd
            : topic;

        if (stompClientRef.current && stompClientRef.current.connected) {
          stompClientRef.current.subscribe(topicToSubscribe, (sdkEvent) => {
            onMessageReceived(sdkEvent);
          });
          subscribe();
        }
      },
      errorCallBack
    );
  };

  const _disconnect = () => {
    if (stompClientRef.current && stompClientRef.current.connected) {
      stompClientRef.current.disconnect(() => {});
    }
  };

  const errorCallBack = () => {
    setTimeout(() => {
      _connect();
    }, 5000);
  };

  const _send = () => {
    if (!stompClientRef.current || !stompClientRef.current.connected) return;

    const sendMessage = {
      method: "SUBSCRIBE",
      params: [
        `${currencycode.toLowerCase()}/${basecurrency.toLowerCase()}@ticker`,
        `${currencycode.toLowerCase()}${basecurrency.toLowerCase()}@depth`,
        `${currencycode.toLowerCase()}${basecurrency.toLowerCase()}@trade`,
      ],
      id: reqNo,
    };

    stompClientRef.current.send(
      currencycode.toUpperCase() === "BTC" &&
        basecurrency.toUpperCase() === "USD"
        ? "/app/sendRequest/BTCUSD"
        : "/app/sendRequest",
      {},
      JSON.stringify(sendMessage)
    );
  };

  const onMessageReceived = (message) => {
    const parsedMessage = JSON.parse(message.body);
    const data = parsedMessage.tR;
    if (data !== null) {
      setChartData(data);
      setLowPrice(parseFloat(data?.lowPrice).toFixed(2));
      setHighPrice(parseFloat(data?.highPrice).toFixed(2));
      setRocData(parseFloat(data?.roc).toFixed(2));
      setVolumnData(parseFloat(data?.volume).toFixed(2));
      setIsNegative(data?.roc < 0);
    }
  };

  useEffect(() => {
    const cleanup = checkInternetConnection(setIsConnected);
    return () => cleanup();
  }, []);

  const subscribe = () => {
    if (currencycode && basecurrency) {
      setTimeout(() => {
        _send();
      }, 10000);
    }
  };

  useEffect(() => {
    const fetchTickerData = () => {
      axios
        .get(
          `https://stream.paybito.com/SocketStream/api/get24hTicker?counter=${currencycode}&base=${basecurrency}`,
          { headers: { "Content-Type": "application/json" } }
        )
        .then((response) => {
          const result = response.data;
          if (result && result.length) {
            const data = result[0];
            setChartData(data);
            setLtp(data?.ltp);
            setLowPrice(parseFloat(data.lowPrice).toFixed(2));
            setHighPrice(parseFloat(data.highPrice).toFixed(2));
            setRocData(parseFloat(data.roc).toFixed(2));
            setVolumnData(parseFloat(data.volume).toFixed(2));
            setIsNegative(data.roc < 0);
          }
          _disconnect();
          _connect();
        })
        .catch((error) => {
          console.error("Error fetching ticker data:", error);
        });
    };

    fetchTickerData();

    return () => {
      _disconnect();
    };
  }, [basecurrency, currencycode]);

  const handleButtonClick = (button) => {
    setActiveButton(button);
  };

  return (
    <DataContext.Provider value={allProps}>
      <Paper
        className="body-page-container"
        style={{
          minHeight: "100%",
          padding: "1px 10px",
          overflowX: "hidden",
        }}
      >
        <Row className="LetfTable px-1 py-2" style={{ margin: 0 }}>
          <Col
            xs={12}
            sm={12}
            md={2}
            className="mb-1 position-relative p-0 orderbooktable"
          >
            <LeftTable />
          </Col>
          <Col xs={12} sm={12} md={8} className="mb-1 px-1 spotchart">
            <div className="d-flex justify-content-center">
              <p className="d-flex align-items-center">
                Last Price :
                <span
                  style={{ marginLeft: "10px", color: "rgb(241, 185, 0)" }}
                >
                  <FlipNumbers
                    height={12}
                    width={7}
                    play
                    duration={2}
                    numbers={Number(chartData?.ltp).toLocaleString(undefined, {
                      minimumFractionDigits: 6,
                      maximumFractionDigits: 6,
                    })}
                  />
                </span>
              </p>
              <p>
                24H ROC :{" "}
                <span style={{ color: isNegative ? "#F5475D" : "#21CB80" }}>
                  {rocData}%
                </span>
              </p>
              <p>
                24H Volume {`(${basecurrency.toUpperCase()})`} :{" "}
                <span>&nbsp;{volumnData}</span>
              </p>
            </div>
            <div style={{ marginTop: "7px" }}>
              <div
                style={{
                  backgroundColor: "var(--body-color-less)",
                  padding: "3px 5px",
                  marginBottom: "3px",
                }}
              >
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic example"
                >
                  <button
                    type="button"
                    style={{
                      color: "white",
                      padding: "7px 10px",
                      fontWeight: "600",
                    }}
                    className={`allbtn ${
                      activeButton === "Price" ? "active" : ""
                    }`}
                    onClick={() => handleButtonClick("Price")}
                  >
                    Price
                  </button>
                  <button
                    type="button"
                    className={`allbtn ${
                      activeButton === "Depth" ? "active" : ""
                    }`}
                    style={{ padding: "7px 10px", fontWeight: "600" }}
                    onClick={() => handleButtonClick("Depth")}
                  >
                    Depth
                  </button>
                </div>
              </div>
              {activeButton === "Price" ? <TvChartContainer /> : <Depth />}
            </div>
            <div style={{ position: "relative" }}>
              <BuyAndSell />
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 10,
                  cursor: "not-allowed",
                }}
                title="Login to trade"
              />
            </div>
          </Col>
          <Col xs={12} sm={12} md={2} className="mb-1 p-0 orderbooktable">
            <RightTable />
          </Col>
        </Row>
        <Col xs={12} md={12} className="mb-4 p-0" style={{ position: "relative" }}>
          <BottomTable />
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              cursor: "not-allowed",
            }}
            title="Login to view your orders"
          />
        </Col>
        <div className="connectioncheck">
          {isConnected && (
            <span style={{ color: "#21CB80" }}>
              <SignalCellularAltIcon fontSize="12px" /> Stable Connection
            </span>
          )}
          {!isConnected && (
            <span style={{ color: "#F5475D" }}>
              <SignalCellularAltIcon fontSize="12px" /> Not Connected
            </span>
          )}
        </div>
      </Paper>
    </DataContext.Provider>
  );
};

export default SpotPublic;
